import API_BASE from '../config';
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check, X, Minus, ChevronRight, ChevronDown,
  Shield, BarChart3, Globe, Zap, Users, Lock,
  FileText, Eye,
  ArrowRight, Star
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// ─── Types ────────────────────────────────────────────────────────────────────

type PlanKey = "starter" | "professional" | "enterprise";
type CellValue = boolean | "partial" | string;

interface Feature {
  name:         string;
  description:  string;
  starter:      CellValue;
  professional: CellValue;
  enterprise:   CellValue;
}

interface FeatureGroup {
  title:    string;
  icon:     React.ElementType;
  features: Feature[];
}

// ─── Plan data ────────────────────────────────────────────────────────────────

const plans: Record<PlanKey, { label: string; monthly: number; annual: number; color: string; highlight: boolean }> = {
  starter: {
    label:     "Starter",
    monthly:   199,
    annual:    159,
    color:     "text-slate-600",
    highlight: false,
  },
  professional: {
    label:     "Professional",
    monthly:   499,
    annual:    399,
    color:     "text-blue-600",
    highlight: true,
  },
  enterprise: {
    label:     "Enterprise",
    monthly:   999,
    annual:    799,
    color:     "text-amber-600",
    highlight: false,
  },
};

// ─── Feature groups ───────────────────────────────────────────────────────────

const featureGroups: FeatureGroup[] = [
  {
    title: "Risk Assessment",
    icon:  Shield,
    features: [
      {
        name:         "Entity Assessments",
        description:  "Number of unique companies you can run a full cyber risk assessment for. Each assessment covers 34 technical control points across 12 security domains.",
        starter:      "1 company",
        professional: "2 companies",
        enterprise:   "5 companies",
      },
      {
        name:         "12-Stage Assessment Form",
        description:  "Comprehensive 12-stage audit covering Entity Profile, Identity & Access, Cloud Security, Data Protection, Network Defense, Recovery Ops, Vendor Risk, Global Compliance, API & Mobile, Human Firewall, Physical Assets and IR & Recovery.",
        starter:      true,
        professional: true,
        enterprise:   true,
      },
      {
        name:         "Security Posture Score",
        description:  "A composite risk score from 0-100 calibrated against NIST CSF maturity tiers. Updated every time you submit an assessment.",
        starter:      true,
        professional: true,
        enterprise:   true,
      },
      {
        name:         "NIST CSF Maturity Tier",
        description:  "Automated mapping of your security posture to one of four NIST Cybersecurity Framework maturity tiers Partial, Risk Informed, Repeatable, Adaptive.",
        starter:      true,
        professional: true,
        enterprise:   true,
      },
      {
        name:         "Risk Score History & Trend Chart",
        description:  "Every assessment submission saves a risk score snapshot. The dashboard displays a trend line showing posture improvement over time useful for board reporting.",
        starter:      true,
        professional: true,
        enterprise:   true,
      },
    ],
  },
  {
    title: "Financial Intelligence",
    icon:  BarChart3,
    features: [
      {
        name:         "Annual Financial Exposure",
        description:  "Calculates your organisation's maximum projected annual financial loss across 8 threat vectors using sector-specific base impact figures from IBM Cost of a Data Breach 2024.",
        starter:      true,
        professional: true,
        enterprise:   true,
      },
      {
        name:         "Monte Carlo Simulations",
        description:  "10,000 simulations run per assessment to produce statistically valid exposure ranges rather than single-point estimates. The same methodology used by Marsh and Aon.",
        starter:      false,
        professional: true,
        enterprise:   true,
      },
      {
        name:         "P90 & P99 Confidence Intervals",
        description:  "P90 shows the figure you have a 10% chance of exceeding. P99 shows the worst-case scenario (1% probability). Essential for actuarial and insurance purposes.",
        starter:      false,
        professional: true,
        enterprise:   true,
      },
      {
        name:         "Threat Vector Impact Chart",
        description:  "Visualises expected annual loss per threat vector, Data Breach, Ransomware, Phishing, API Breach, Payment Fraud, Vendor Breach, Insider Threat and DDoS sorted highest to lowest.",
        starter:      true,
        professional: true,
        enterprise:   true,
      },
      {
        name:         "Sector Benchmarking",
        description:  "Compares your financial exposure and security posture against sector peers using IBM Cost of a Data Breach 2024 and Verizon DBIR 2024 sector-specific data.",
        starter:      false,
        professional: true,
        enterprise:   true,
      },
    ],
  },
  {
    title: "Reports & Documents",
    icon:  FileText,
    features: [
      {
        name:         "Audit PDF Report",
        description:  "Starter plan receives an 8-page audit report covering financial exposure, threat surface, regulatory compliance and remediation. Professional and Enterprise receive the full 13-page executive report including Monte Carlo intervals, vendor matrix, insurance ROI and executive ROI summary.",
        starter:      "8-page report",
        professional: "Full 13-page report",
        enterprise:   "Full 13-page report",
      },
      {
        name:         "19-Slide Board Intelligence Deck",
        description:  "A landscape-format PDF presentation designed for board and executive presentations. Includes actuarial risk math, financial heatmaps, remediation narrative across 4 slides, phased 12-month timeline, technical roadmap, board disclosure and certification. Covers all 8 AI-generated remediation paragraphs.",
        starter:      false,
        professional: false,
        enterprise:   true,
      },
      {
        name:         "VicShield Strategic Remediation",
        description:  "VicShield generates a bespoke multi-paragraph remediation narrative tailored to your specific audit answers, sector, and risk profile. Included across all plans Standard and Professional receive 3 pages, Enterprise deck includes all 8 paragraphs across 4 dedicated slides.",
        starter:      true,
        professional: true,
        enterprise:   true,
      },
      {
        name:         "Report Download History",
        description:  "Full audit trail of every report generated report type, date, and verification ID. Available to all plans.",
        starter:      true,
        professional: true,
        enterprise:   true,
      },
    ],
  },
  {
    title: "Threat Intelligence",
    icon:  Eye,
    features: [
      {
        name:         "Credential Breach & Dark Web Intelligence",
        description:  "Full credential breach scanning, dark web monitoring and external threat surface analysis across all plans. Launching soon all subscribers will be notified when live.",
        starter:      "coming soon",
        professional: "coming soon",
        enterprise:   "coming soon",
      },
    ],
  },
  {
    title: "Compliance Intelligence",
    icon:  Globe,
    features: [
      {
        name:         "Regulatory Radar",
        description:  "Automatically maps your sector, geography and revenue currency to applicable regulatory frameworks. Monitors Monitors 356+ regulatory frameworks across 34 countries & jurisdictions such as GDPR, FCA, DORA, PCI-DSS, SEC, HIPAA, NIS2 and more.",
        starter:      true,
        professional: true,
        enterprise:   true,
      },
      {
        name:         "Active Compliance Alerts",
        description:  "Surfaces active regulatory requirements with deadline countdown, fine risk quantification and urgency rating. Updated whenever legislative changes affect your entity profile.",
        starter:      true,
        professional: true,
        enterprise:   true,
      },
      {
        name:         "VicShield Compliance Intelligence",
        description:  "Bespoke gap analysis per active regulation. Maps your specific audit answers against each regulatory requirement, produces a compliance score, identifies exact gaps, estimates remediation cost and generates a prioritised action plan with timelines. Available on Professional and Enterprise.",
        starter:      false,
        professional: true,
        enterprise:   true,
      },
    ],
  },
  {
    title: "Insurance Intelligence",
    icon:  Zap,
    features: [
      {
        name:         "Insurance Premium Modelling",
        description:  "Estimates your annual cyber insurance premium based on your risk profile, then projects savings achievable by implementing the remediation roadmap. Aligned to international cyber insurance underwriting criteria across major markets globally.",
        starter:      true,
        professional: true,
        enterprise:   true,
      },
      {
        name:         "VicShield Insurance Intelligence",
        description:  "Bespoke underwriting analysis mapped to your audit profile. Identifies exactly which controls are driving your premium up, what to fix to drop into a lower actuarial tier, estimated savings per improvement, coverage implications and direct underwriter perspective on your biggest risk factors. Available on Professional and Enterprise.",
        starter:      false,
        professional: true,
        enterprise:   true,
      },
      {
        name:         "Insurability Eligibility Checklist",
        description:  "Visual checklist showing which underwriting criteria your organisation currently meets MFA, data resilience, asset encryption and which are missing.",
        starter:      true,
        professional: true,
        enterprise:   true,
      },
    ],
  },
  {
    title: "Vendor Risk Management",
    icon:  Users,
    features: [
      {
        name:         "Vendor Registry",
        description:  "Add and manage your third-party vendor ecosystem. Each vendor is scored against SOC2, ISO 27001, GDPR compliance and recent breach history.",
        starter:      true,
        professional: true,
        enterprise:   true,
      },
      {
        name:         "Automated Vendor Scoring",
        description:  "VicShield automatically calculates a risk score (0-100) and risk status (Low / Medium / High / Critical) for each vendor based on their security certifications and breach history.",
        starter:      true,
        professional: true,
        enterprise:   true,
      },
      {
        name:         "Vendor Risk in Audit Report",
        description:  "Your registered vendor list is automatically included in the Section V of your PDF audit report with scores and risk status no manual data entry required.",
        starter:      true,
        professional: true,
        enterprise:   true,
      },
    ],
  },
  {
    title: "Platform & Security",
    icon:  Lock,
    features: [
      {
        name:         "Two-Factor Authentication (MFA)",
        description:  "TOTP-based two-factor authentication compatible with Google Authenticator and Authy. Adds a mandatory second verification step at login to protect your intelligence data.",
        starter:      true,
        professional: true,
        enterprise:   true,
      },
      {
        name:         "Immutable Audit Trail",
        description:  "Every significant action,assessment submissions, report downloads, vendor changes, team invites, API key generation is recorded in an append-only audit log with timestamp, IP address and actor.",
        starter:      true,
        professional: true,
        enterprise:   true,
      },
      {
        name:         "Team Member Access",
        description:  "Invite colleagues with role-based access control. Admin (full access), Analyst (assessments and reports), Viewer (read-only). Professional supports 2 team members. Enterprise supports 10.",
        starter:      false,
        professional: "2 members",
        enterprise:   "10 members",
      },
      {
        name:         "API Access",
        description:  "Programmatic access to your VicShield intelligence data via REST API using a secret API key. Pull risk scores, compliance status, vendor data and history into PowerBI, Splunk, Tableau or any custom dashboard. Up to 5 API keys per account.",
        starter:      false,
        professional: true,
        enterprise:   true,
      },
    ],
  },
];

// ─── FAQ data ─────────────────────────────────────────────────────────────────

const faqs = [
  {
    q: "Can I upgrade my plan at any time?",
    a: "Yes. Upgrades take effect immediately. You will be charged the new plan price on the day of upgrade and your billing date resets. Remaining credit from your current plan is not carried over, which we show transparently before you confirm.",
  },
  {
    q: "What happens to my data if I cancel?",
    a: "Your data is retained for 14 days after cancellation. During this period you can reactivate your account and access everything. After 14 days, all data is permanently deleted from our systems.",
  },
  {
    q: "Can I use the API to integrate VicShield into PowerBI or Splunk?",
    a: "Yes. Enterprise plan includes full REST API access. You can pull your risk score, compliance status, vendor data and risk score history into any external platform using your API key in the X-API-Key header. Available endpoints: /v1/risk-score, /v1/compliance-status, /v1/vendors, /v1/history. Full documentation is available in your Settings page.",
  },
  {
    q: "Is there a discount for annual billing?",
    a: "Yes. All plans are 20% cheaper on annual billing. Starter drops from $199/mo to $159/mo. Professional from $499/mo to $399/mo. Enterprise from $999/mo to $799/mo. You can switch between monthly and annual billing at any time.",
  },
  {
    q: "How is the financial exposure figure calculated?",
    a: "We use the FAIR (Factor Analysis of Information Risk) actuarial methodology, calibrated against IBM Cost of a Data Breach 2024 and Verizon DBIR 2024 sector-specific data. Your exposure figure is produced by running 10,000 Monte Carlo simulations across 8 threat vectors, adjusted for your sector, employee count, revenue, and the 34 technical control answers from your assessment.",
  },
  {
    q: "Is my company data shared with third parties?",
    a: "No. Your assessment data, financial figures, and intelligence reports are never sold or shared with any third party. VicShield uses your data exclusively to generate your risk intelligence. We do not display advertising and are not ad-supported.",
  },
  {
    q: "What currencies do you support?",
    a: "We support 50+ currencies including GBP, USD, EUR, AED, NGN, ZAR, INR, CAD, AUD and many more. All financial exposure figures, premiums and savings are displayed in your chosen currency.",
  },
  {
    q: "What is the difference between masked and full dark web results?",
    a: "Standard and Professional plans show credential leaks with the identity partially masked (e.g. j***@company.com) so you know a leak exists but cannot see the exact affected account. Enterprise plan unlocks full plaintext identity recovery — you see the exact email address and can take immediate remediation action.",
  },
  {
    q: "Do you offer refunds?",
    a: "We offer a 7-day refund on your first payment if you are not satisfied. After 7 days, all payments are non-refundable but you retain full access until the end of your billing period. No questions asked on refund requests within the 7-day window.",
  },
  {
    q: "What is VicShield Compliance Intelligence?",
    a: "VicShield Compliance Intelligence is available on Professional and Enterprise plans. It generates a bespoke regulatory gap analysis for each active regulation in your jurisdiction. It maps your specific audit answers against regulatory requirements, produces a compliance score per regulation, identifies exact gaps, estimates remediation cost and generates a prioritised action plan with deadlines. Available for GDPR, FCA Operational Resilience, DORA, PCI-DSS, SEC, HIPAA and all other active frameworks.",
  },
  {
    q: "What is VicShield Insurance Intelligence?",
    a: "VicShield Insurance Intelligence is available on Professional and Enterprise plans. It generates a bespoke underwriting analysis mapped directly to your audit profile. It identifies exactly which controls are driving your premium up, what specific fixes will move you into a lower actuarial tier, the estimated premium reduction per improvement, coverage implications including limits, exclusions and excess, and a direct underwriter perspective on your biggest risk factors. Calibrated against Lloyd's, AIG and Beazley underwriting criteria.",
  },
];

// ─── Cell renderer ────────────────────────────────────────────────────────────

function Cell({ value }: { value: CellValue }) {
  if (value === true) {
    return <Check size={20} strokeWidth={3} className="text-emerald-500 mx-auto" />;
  }
  if (value === false) {
    return <X size={18} strokeWidth={3} className="text-red-500 mx-auto" />;
  }
  if (value === "partial") {
    return <Minus size={18} strokeWidth={3} className="text-amber-400 mx-auto" />;
  }
  return (
    <span className="text-xs font-black text-slate-700 uppercase tracking-wide text-center block">
      {value}
    </span>
  );
}

// ─── FAQ Item ─────────────────────────────────────────────────────────────────

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left gap-4"
      >
        <span className="text-sm font-black text-slate-900 uppercase tracking-tight">
          {q}
        </span>
        <ChevronDown
          size={18}
          className={`text-slate-400 flex-shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="text-slate-500 text-sm font-medium leading-relaxed pb-5">
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Pricing() {
  const navigate        = useNavigate();
  const [annual, setAnnual] = useState(false);

  const handleGetStarted = (tier: string) => {
    navigate("/signup", { state: { initialMode: "signup", selectedTier: tier } });
  };

  return (
    <div className="bg-white font-['Inter'] text-slate-900">

      {/* ── NAV ─────────────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div
            onClick={() => navigate("/")}
            className="flex items-center gap-3 cursor-pointer group"
          >
            <div className="w-11 h-11 bg-red-600 rounded-xl flex items-center justify-center
              shadow-lg shadow-red-200 group-hover:scale-105 transition-transform">
              <span className="text-white font-black text-lg">V</span>
            </div>
            <span className="text-2xl font-black tracking-tighter text-slate-900 uppercase">
              VicShield
            </span>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate("/")}
              className="text-xs font-black uppercase tracking-widest text-slate-400
                hover:text-slate-900 transition-colors">
              ← Back to Home
            </button>
            <button
              onClick={() => handleGetStarted("Starter")}
              className="bg-red-600 text-white px-6 py-2.5 rounded-xl text-[11px] font-black
                uppercase tracking-widest hover:bg-slate-900 shadow-md shadow-red-100
                transition-all active:scale-95"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* ── HERO ────────────────────────────────────────────────────── */}
      <section className="bg-[#0f172a] py-20 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
          w-[600px] h-[600px] bg-red-600 opacity-[0.06] rounded-full blur-[120px]
          pointer-events-none" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-500 mb-4">
            Transparent Pricing
          </p>
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tighter
            uppercase mb-6">
            Everything you get with VicShield
          </h1>
          <p className="text-slate-400 font-medium leading-relaxed max-w-2xl mx-auto mb-10">
            No hidden fees. No vague feature descriptions. Every capability explained
            in plain language so you know exactly what you are buying.
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center gap-4 bg-white/5 border border-white/10
            rounded-2xl p-1.5">
            <button
              onClick={() => setAnnual(false)}
              className={`px-6 py-2.5 rounded-xl text-[11px] font-black uppercase
                tracking-widest transition-all ${
                !annual
                  ? "bg-white text-slate-900 shadow-md"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`px-6 py-2.5 rounded-xl text-[11px] font-black uppercase
                tracking-widest transition-all flex items-center gap-2 ${
                annual
                  ? "bg-white text-slate-900 shadow-md"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              Annually
              {!annual && (
                <span className="bg-emerald-500 text-white text-[9px] font-black
                  uppercase px-2 py-0.5 rounded-full">
                  Save 20%
                </span>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* ── PLAN CARDS ──────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {(Object.entries(plans) as [PlanKey, typeof plans[PlanKey]][]).map(([key, plan]) => (
            <div
              key={key}
              className={`rounded-[2.5rem] border-2 p-8 ${
                plan.highlight
                  ? "bg-[#0f172a] border-red-600 shadow-2xl"
                  : "bg-white border-slate-100"
              }`}
            >
              {plan.highlight && (
                <div className="flex items-center gap-2 mb-4">
                  <Star size={14} className="text-red-500 fill-red-500" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-red-500">
                    Most Popular
                  </span>
                </div>
              )}
              <h3 className={`text-xs font-black uppercase tracking-widest mb-2 ${plan.color}`}>
                {plan.label}
              </h3>
              <div className="flex items-baseline gap-1 mb-1">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={annual ? "annual" : "monthly"}
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    className={`text-4xl font-black tracking-tighter ${
                      plan.highlight ? "text-white" : "text-slate-900"
                    }`}
                  >
                    ${annual ? plan.annual : plan.monthly}
                  </motion.span>
                </AnimatePresence>
                <span className={`text-sm font-bold ${
                  plan.highlight ? "text-slate-400" : "text-slate-900"
                }`}>/mo</span>
              </div>
              {annual && (
                <p className="text-[10px] font-bold text-emerald-500 mb-4">
                  Billed ${(annual ? plan.annual : plan.monthly) * 12}/yr
                </p>
              )}
              <button
                onClick={() => handleGetStarted(plan.label)}
                className={`w-full py-3.5 rounded-2xl font-black text-[11px] uppercase
                  tracking-widest transition-all mt-4 flex items-center justify-center gap-2 ${
                  plan.highlight
                    ? "bg-red-600 text-white hover:bg-white hover:text-red-600"
                    : "bg-slate-900 text-white hover:bg-red-600"
                }`}
              >
                Get Started <ArrowRight size={14} />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURE COMPARISON TABLE ─────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="mb-10 text-center">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-600 mb-3">
            Full Feature Breakdown
          </p>
          <h2 className="text-3xl font-black tracking-tighter uppercase">
            Compare every feature in detail
          </h2>
        </div>

        {/* Feature breakdown table - horizontally scrollable on mobile */}
        <div className="overflow-x-auto">
          <div className="min-w-[640px]">

            {/* Plan header */}
            <div className="grid grid-cols-4 gap-4 py-5 bg-white border-b-2 border-slate-900 mb-4">
              <div />
              {(Object.entries(plans) as [PlanKey, typeof plans[PlanKey]][]).map(([key, plan]) => (
                <div key={key} className="text-center">
                  <p className={`text-sm font-black uppercase tracking-widest ${plan.color}`}>
                    {plan.label}
                  </p>
                  <p className="text-xs font-bold text-slate-900 mt-1">
                    ${annual ? plan.annual : plan.monthly}/mo
                  </p>
                </div>
              ))}
            </div>

            {/* Feature groups */}
            {featureGroups.map((group, gi) => (
            <div key={gi} className="mb-8">
            {/* Group header */}
            <div className="flex items-center gap-3 py-4 border-b-2 border-slate-900 mb-2">
              <div className="w-8 h-8 bg-slate-900 rounded-xl flex items-center
                justify-center flex-shrink-0">
                <group.icon size={16} className="text-white" />
              </div>
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">
                {group.title}
              </h3>
            </div>

            {/* Feature rows */}
            {group.features.map((feature, fi) => (
              <div
                key={fi}
                className={`grid grid-cols-4 gap-4 py-6 border-b border-slate-100
                  transition-colors ${
                  fi % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                }`}
              >
                {/* Feature name + description */}
                <div className="pr-4">
                  <p className="text-xs font-black text-slate-900 uppercase tracking-tight mb-2">
                    {feature.name}
                  </p>
                  <p className="text-xs font-medium text-slate-500 leading-relaxed">
                    {feature.description}
                  </p>
                </div>

                {/* Plan cells */}
                <div className="flex items-center justify-center">
                  <Cell value={feature.starter} />
                </div>
                <div className={`flex items-center justify-center rounded-xl ${
                  plans.professional.highlight ? "bg-[#0f172a]/5" : ""
                }`}>
                  <Cell value={feature.professional} />
                </div>
                <div className="flex items-center justify-center">
                  <Cell value={feature.enterprise} />
                </div>
              </div>
            ))}
          </div>
          ))}
          </div>
        </div>
      </section>

      {/* ── API INTEGRATION SECTION ──────────────────────────────────── */}
      <section className="bg-[#0f172a] py-20 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-red-600 opacity-[0.05]
          rounded-full blur-[100px] pointer-events-none" />
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-500 mb-4">
                Enterprise API Access
              </p>
              <h2 className="text-3xl font-black text-white tracking-tighter uppercase mb-6">
                Integrate VicShield into your existing security stack
              </h2>
              <p className="text-slate-400 font-medium leading-relaxed mb-8">
                Enterprise subscribers get full REST API access. Pull your VicShield
                risk intelligence into any external platform automatically no manual
                data exports, no browser required.
              </p>
              <div className="space-y-4 mb-10">
                {[
                  { tool: "Microsoft PowerBI", desc: "Display your live risk score on executive dashboards alongside financial KPIs" },
                  { tool: "Splunk",            desc: "Feed threat intelligence into your SIEM for unified security monitoring" },
                  { tool: "Tableau",           desc: "Visualise risk trend data and compliance status in custom reporting views" },
                  { tool: "Custom Dashboard",  desc: "Build your own internal portal using our simple REST API with any language" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-2 h-2 bg-red-600 rounded-full flex-shrink-0 mt-1.5" />
                    <div>
                      <span className="text-white font-black text-sm">{item.tool} — </span>
                      <span className="text-slate-400 text-sm font-medium">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => handleGetStarted("Enterprise")}
                className="flex items-center gap-2 bg-red-600 text-white px-8 py-4
                  rounded-2xl font-black text-[11px] uppercase tracking-widest
                  hover:bg-white hover:text-red-600 transition-all shadow-xl"
              >
                Get Enterprise Access <ChevronRight size={15} />
              </button>
            </div>

            {/* Code example */}
            <div className="bg-slate-900 rounded-[2rem] p-8 border border-white/5
              shadow-2xl font-mono text-sm">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-3 h-3 bg-red-500 rounded-full" />
                <div className="w-3 h-3 bg-amber-400 rounded-full" />
                <div className="w-3 h-3 bg-emerald-400 rounded-full" />
                <span className="text-slate-500 text-xs ml-2 font-sans">
                  PowerBI / Splunk Integration
                </span>
              </div>
              <pre className="text-xs leading-relaxed overflow-x-auto">
                <span className="text-slate-500"># Pull risk score into any platform</span>{"\n"}
                <span className="text-blue-400">GET</span>
                <span className="text-white"> ${API_BASE}</span>{"\n"}
                <span className="text-white">    /v1/risk-score</span>{"\n\n"}
                <span className="text-slate-500"># Headers</span>{"\n"}
                <span className="text-amber-400">X-API-Key</span>
                <span className="text-white">: VS-API-xxxxxxxxxxxx</span>{"\n\n"}
                <span className="text-slate-500"># Response</span>{"\n"}
                <span className="text-white">{"{"}</span>{"\n"}
                <span className="text-emerald-400">  "company"</span>
                <span className="text-white">: </span>
                <span className="text-amber-300">"Wedland Ltd"</span>
                <span className="text-white">,</span>{"\n"}
                <span className="text-emerald-400">  "risk_score"</span>
                <span className="text-white">: </span>
                <span className="text-blue-300">42</span>
                <span className="text-white">,</span>{"\n"}
                <span className="text-emerald-400">  "total_exposure"</span>
                <span className="text-white">: </span>
                <span className="text-blue-300">34029</span>
                <span className="text-white">,</span>{"\n"}
                <span className="text-emerald-400">  "currency"</span>
                <span className="text-white">: </span>
                <span className="text-amber-300">"GBP"</span>
                <span className="text-white">,</span>{"\n"}
                <span className="text-emerald-400">  "nist_tier"</span>
                <span className="text-white">: </span>
                <span className="text-blue-300">2</span>{"\n"}
                <span className="text-white">{"}"}</span>
              </pre>
              <div className="mt-6 pt-6 border-t border-white/5 space-y-2">
                {[
                  "/v1/risk-score",
                  "/v1/compliance-status",
                  "/v1/vendors",
                  "/v1/history",
                ].map((ep, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-[9px] font-black bg-emerald-500/20 text-emerald-400
                      px-2 py-0.5 rounded uppercase tracking-widest">
                      GET
                    </span>
                    <span className="text-slate-400 text-xs">{ep}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────── */}
      <section className="py-20 max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-600 mb-3">
            Common Questions
          </p>
          <h2 className="text-3xl font-black tracking-tighter uppercase">
            Frequently asked questions
          </h2>
        </div>
        <div className="bg-white rounded-[2.5rem] border-2 border-slate-100 px-10 py-2
          shadow-sm">
          {faqs.map((faq, i) => (
            <FaqItem key={i} q={faq.q} a={faq.a} />
          ))}
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────── */}
      <section className="bg-[#0f172a] py-20 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
          w-[500px] h-[500px] bg-red-600 opacity-[0.06] rounded-full blur-[100px]
          pointer-events-none" />
        <div className="relative z-10 text-center max-w-2xl mx-auto px-6">
          <h2 className="text-4xl font-black text-white tracking-tighter uppercase mb-6">
            Ready to quantify your risk?
          </h2>
          <p className="text-slate-400 font-medium leading-relaxed mb-10">
            Join enterprise security teams who have transformed their cyber risk
            posture from guesswork into precise, board-ready intelligence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => handleGetStarted("Starter")}
              className="bg-red-600 text-white px-10 py-4 rounded-2xl font-black text-sm
                uppercase tracking-widest hover:bg-white hover:text-red-600
                transition-all shadow-xl flex items-center justify-center gap-3"
            >
              Start Free Assessment <ArrowRight size={16} />
            </button>
            <button
              onClick={() => handleGetStarted("Enterprise")}
              className="border-2 border-white/20 text-white px-10 py-4 rounded-2xl
                font-black text-sm uppercase tracking-widest hover:border-white/60
                transition-all flex items-center justify-center gap-3"
            >
              Book a Demo <ChevronRight size={16} />
            </button>
          </div>
          <p className="mt-4 text-[10px] font-bold text-slate-600 uppercase tracking-widest">
            7-day trial · card not charged until day 8 · Cancel anytime
          </p>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────── */}
      <footer className="bg-[#0f172a] border-t border-white/5 py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between
          items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-red-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-sm">V</span>
            </div>
            <span className="text-slate-500 text-[10px] font-black uppercase tracking-widest">
              © 2026 VicShield Global. All rights reserved.
            </span>
          </div>
          <div className="flex gap-6">
            <button onClick={() => navigate("/")}
              className="text-[10px] font-black uppercase tracking-widest
                text-slate-600 hover:text-red-500 transition-colors">
              Home
            </button>
            <button
              onClick={() => navigate("/privacy")}
              className="text-[10px] font-black uppercase tracking-widest
                text-slate-600 hover:text-red-500 transition-colors">
              Privacy Policy
            </button>
            <button
              onClick={() => navigate("/terms")}
              className="text-[10px] font-black uppercase tracking-widest
                text-slate-600 hover:text-red-500 transition-colors">
              Terms of Service
            </button>
          </div>
        </div>
      </footer>

    </div>
  );
}
